import { useSEO } from "@/lib/useSEO";

const TITLE = "Privacy Policy | Parlay Assistant";
const DESCRIPTION =
  "How Parlay Assistant handles analytics, cookies, sponsor links, and user data across its free sports betting calculators.";
const LAST_UPDATED = "February 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-8">
    <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight text-slate-900">
      {title}
    </h2>
    <div className="mt-3 text-slate-700 text-sm md:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

export const PrivacyPage = () => {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/privacy" });

  return (
    <main
      data-testid="privacy-page"
      className="max-w-4xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
        Legal
      </p>
      <h1 className="mt-2 font-display text-white text-4xl sm:text-5xl uppercase tracking-tight leading-[1.02]">
        Privacy Policy
      </h1>
      <p className="mt-3 text-slate-400 text-sm">Last Updated: {LAST_UPDATED}</p>

      <div className="mt-8 pa-card p-6 md:p-10">
        <p className="text-slate-700 text-sm md:text-base leading-relaxed">
          This Privacy Policy explains how Parlay Assistant (&ldquo;we,&rdquo; &ldquo;our,&rdquo;
          or &ldquo;us&rdquo;) handles information when you visit our website and use our free
          betting calculators and tools. By using Parlay Assistant, you agree to the practices
          described below.
        </p>

        <Section id="information-we-collect" title="Information We Collect">
          <p>
            Parlay Assistant does not require you to create an account, and we do not knowingly
            collect sensitive personal information through our calculators. The bet amounts,
            odds, and other inputs you type into the tools stay in your browser and are not
            transmitted to our servers.
          </p>
          <p>
            When you contact us through the &ldquo;Advertise With Us&rdquo; form we collect only
            the information you provide (name, email, and any details you choose to share about
            your inquiry) so we can respond to you.
          </p>
        </Section>

        <Section id="analytics" title="Analytics">
          <p>
            We use PostHog product analytics and Google Analytics 4 to understand how visitors
            interact with our tools (for example, which calculators are used most, which sponsor
            cards get clicked, and when the &ldquo;Advertise With Us&rdquo; button is opened).
            PostHog is configured in an &ldquo;identified only&rdquo; mode, meaning we do not
            associate anonymous visitors with personal identifiers. Google Analytics only
            receives standard page-view events (page path, page title, and referrer) triggered
            by route changes.
          </p>
          <p>
            We deliberately strip PII from analytics events. We do not send bet amounts, odds
            entered, selection labels, names, email addresses, phone numbers, or IP addresses to
            PostHog or Google Analytics.
          </p>
        </Section>

        <Section id="cookies" title="Cookies">
          <p>
            Parlay Assistant uses first-party cookies and browser storage only where necessary
            for basic site functionality and for our analytics provider to distinguish repeat
            visits. You may disable cookies in your browser at any time; the calculators will
            continue to work.
          </p>
        </Section>

        <Section id="third-party-services" title="Third-Party Services">
          <p>
            We rely on a small number of third-party services to operate the site, including
            font delivery, analytics (PostHog and Google Analytics 4), and content delivery
            networks. Each provider has its own privacy policy, and we encourage you to review
            them.
          </p>
        </Section>

        <Section id="sponsor-links" title="Sponsor Links">
          <p>
            Our Sponsors page lists third-party partners and communities. When you click a
            sponsor link you leave Parlay Assistant and are subject to the destination
            website&rsquo;s privacy practices and terms. We do not control third-party sites and
            are not responsible for their content or data handling.
          </p>
        </Section>

        <Section id="affiliate-links" title="Affiliate Links (Future Use)">
          <p>
            Parlay Assistant may include affiliate links in the future. If we earn a commission
            from a link, that will be disclosed clearly. Affiliate participation does not
            influence the accuracy of our calculators or educational content.
          </p>
        </Section>

        <Section id="data-security" title="Data Security">
          <p>
            We take reasonable technical measures to protect the limited information we do
            collect (such as advertiser inquiries) against unauthorized access, alteration, or
            destruction. No online service can be guaranteed 100% secure, and we cannot warrant
            the absolute security of any information transmitted to us.
          </p>
        </Section>

        <Section id="childrens-privacy" title="Children's Privacy">
          <p>
            Parlay Assistant is intended for adults who meet the legal gambling age in their
            jurisdiction. We do not knowingly collect information from anyone under 18 (or the
            local age of majority). If you believe a minor has provided us with information,
            please contact us and we will delete it.
          </p>
        </Section>

        <Section id="user-rights" title="User Rights">
          <p>
            Depending on your jurisdiction you may have rights under laws such as GDPR or the
            CCPA, including the right to access, correct, or delete personal information we
            hold about you, and to opt out of certain analytics. To exercise these rights,
            contact us at the address below.
          </p>
        </Section>

        <Section id="changes" title="Changes to this Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the
            &ldquo;Last Updated&rdquo; date at the top of the page. Continued use of the site
            after changes are posted constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section id="contact" title="Contact Information">
          <p>
            Questions about this Privacy Policy? Contact us at{" "}
            <a
              href="mailto:advertise@parlayassistant.com"
              className="text-emerald-700 underline hover:text-emerald-800"
            >
              advertise@parlayassistant.com
            </a>
            .
          </p>
        </Section>
      </div>
    </main>
  );
};

export default PrivacyPage;
